<template>
  <aside class="promo-config">
    <div class="config-body">
      <section class="config-section">
        <div class="section-row">
          <label class="section-label">镜头提示词</label>
          <div class="section-actions">
            <button
              class="pill-btn ai-btn"
              :disabled="
                workbench.promo.loadingAll || workbench.promo.planningPrompts
              "
              @click="workbench.planAllLensPrompts"
            >
              {{
                workbench.promo.planningPrompts ? "生成中..." : "AI 一键规划"
              }}
            </button>
            <button class="text-dim" type="button" @click="clearCurrentPrompt">
              清空
            </button>
          </div>
        </div>

        <div ref="lensPickerRef" class="lens-picker">
          <button
            type="button"
            class="lens-picker-trigger"
            :class="{ open: lensMenuOpen }"
            @click.stop="toggleLensMenu"
          >
            <div class="lens-current-main">
              镜头 {{ currentLens.index + 1 }} · {{ currentLens.label }}
            </div>
            <div class="lens-current-meta">
              <span
                class="lens-current-state"
                :class="{ generated: currentLens.generated }"
              >
                {{ currentLens.generated ? "已生成" : "待生成" }}
              </span>
              <svg
                class="lens-arrow"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </div>
          </button>

          <div v-if="lensMenuOpen" class="lens-picker-menu">
            <button
              v-for="option in lensOptions"
              :key="option.key"
              type="button"
              class="lens-option"
              :class="{
                active:
                  !option.isAll &&
                  option.index === workbench.promo.activeEditIdx,
              }"
              @click="selectLens(option)"
            >
              <span class="lens-option-title">{{ option.title }}</span>
              <span
                class="lens-option-state"
                :class="{ generated: option.generated }"
              >
                {{ option.generated ? "已生成" : "待生成" }}
              </span>
            </button>
          </div>
        </div>

        <div class="textarea-wrap">
          <textarea
            v-model="
              workbench.promo.items[workbench.promo.activeEditIdx].customPrompt
            "
            rows="6"
            class="section-textarea"
            :placeholder="`在此输入或修改镜头 ${
              workbench.promo.activeEditIdx + 1
            } 的具体要求...`"
          ></textarea>
          <div class="textarea-meta">
            <span class="badge-muted">ZH/EN</span>
            <span class="text-dim tiny">{{ currentPromptLength }}/1000</span>
          </div>
        </div>
      </section>

      <section class="config-section">
        <div class="section-row">
          <label class="section-label">构图参考</label>
          <div class="tab-switch wide">
            <button
              class="tab-btn"
              :class="{ active: workbench.promoRefTab === 'cut' }"
              @click="workbench.promoRefTab = 'cut'"
            >
              抠图结果
            </button>
            <button
              class="tab-btn"
              :class="{ active: workbench.promoRefTab === 'upload' }"
              @click="workbench.promoRefTab = 'upload'"
            >
              本地上传
            </button>
          </div>
        </div>

        <div class="ref-card">
          <template v-if="workbench.promoRefTab === 'cut'">
            <div v-if="workbench.lastCut" class="ref-thumb large">
              <img :src="workbench.lastCut" alt="last cut" />
              <div class="ref-note">将优先使用最近的抠图结果</div>
            </div>
            <div v-else class="ref-empty">
              暂无抠图结果，请先在抠图工作台完成抠图
            </div>
          </template>

          <template v-else>
            <div
              v-if="!workbench.promo.refPreview && !workbench.promo.refData"
              class="modern-upload"
              @click="triggerPromoUpload"
            >
              <input
                ref="filePromo"
                class="file-input-hidden"
                type="file"
                accept="image/*,.pdf"
                @change="onPromoFile"
              />
              <div class="upload-visual" @click.stop="triggerPromoUpload">
                <div class="upload-glow"></div>
                <div class="upload-icon-bubble">+</div>
              </div>
              <div class="upload-copy" @click.stop="triggerPromoUpload">
                <p class="upload-title">
                  点击上传 <span class="muted">或拖拽文件</span>
                </p>
                <p class="upload-sub">支持 JPG / PNG ，最大 10MB</p>
              </div>
            </div>
            <div v-else class="ref-thumb preview-only">
              <img
                :src="workbench.promo.refPreview || workbench.promo.refData"
                alt="ref"
              />
              <button
                class="clear-pill"
                type="button"
                @click.stop="workbench.clearPromoRef"
              >
                清除上传
              </button>
            </div>
          </template>
        </div>

        <label class="switch inline">
          <input v-model="workbench.promo.useCutRef" type="checkbox" />
          <span class="slider"></span>
          <span class="switch-text">优先使用参考图构图</span>
        </label>
      </section>

      <section class="config-section">
        <div class="section-row">
          <label class="section-label">生成参数</label>
        </div>

        <div class="ratio-row">
          <div class="ratio-cards">
            <button
              v-for="(item, index) in workbench.ratioOptions"
              :key="item.value"
              class="ratio-card"
              :class="{
                active: item.value === workbench.promo.ratio,
                span2: index < 3,
                span3: index >= 3,
              }"
              @click="workbench.setPromoRatio(item.value)"
            >
              <div
                class="ratio-rect"
                :class="'ratio-' + item.value.replace(':', '-')"
              ></div>
              <div class="ratio-label">{{ item.value }}</div>
              <div
                v-if="item.value === workbench.promo.ratio"
                class="ratio-check"
              >
                ✓
              </div>
            </button>
          </div>
        </div>
      </section>
    </div>

    <div class="config-footer">
      <button
        class="primary-btn"
        :disabled="
          workbench.promo.loadingAll ||
          workbench.promo.planningPrompts ||
          !workbench.promoPromptReady
        "
        @click="workbench.generatePromoAll"
      >
        {{
          workbench.promo.loadingAll
            ? "生成中..."
            : workbench.generateButtonText
        }}
      </button>
      <p class="footer-hint">生成 {{ workbench.promo.count }} 张图</p>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useWorkbenchStore } from "@/stores/useWorkbenchStore";

const workbench = useWorkbenchStore();
const filePromo = ref(null);
const lensPickerRef = ref(null);
const lensMenuOpen = ref(false);

const lensOptions = computed(() => [
  {
    key: "all-lens-plan",
    isAll: true,
    index: -1,
    title: "全部镜头生成",
    generated: workbench.promo.items.every(
      (item) => !!(item.customPrompt && item.customPrompt.trim())
    ),
  },
  ...workbench.promo.items.map((item, index) => ({
    key: `lens-${index}`,
    isAll: false,
    index,
    title: `镜头 ${index + 1} · ${
      item.label || (index === 0 ? "主图" : `展示 ${index}`)
    }`,
    generated: !!item.url,
  })),
]);

const currentLens = computed(() => {
  const index = workbench.promo.activeEditIdx;
  const item = workbench.promo.items[index] || {};
  return {
    index,
    label: item.label || (index === 0 ? "主图" : `展示 ${index}`),
    generated: !!item.url,
  };
});

const currentPromptLength = computed(
  () =>
    (workbench.promo.items[workbench.promo.activeEditIdx]?.customPrompt || "")
      .length
);

const clearCurrentPrompt = () => {
  workbench.promo.items[workbench.promo.activeEditIdx].customPrompt = "";
};

const closeLensMenu = () => {
  lensMenuOpen.value = false;
};

const toggleLensMenu = () => {
  lensMenuOpen.value = !lensMenuOpen.value;
};

const selectLens = (option) => {
  if (option?.isAll) {
    closeLensMenu();
    workbench.planAllLensPrompts();
    return;
  }
  workbench.promo.activeEditIdx = option.index;
  closeLensMenu();
};

const triggerPromoUpload = () => {
  filePromo.value?.click();
};

const onPromoFile = async (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    return;
  }
  await workbench.setPromoRefFile(file);
  event.target.value = "";
};

const onWindowClick = (event) => {
  if (!lensPickerRef.value) {
    return;
  }
  if (!lensPickerRef.value.contains(event.target)) {
    closeLensMenu();
  }
};

const onKeydown = (event) => {
  if (event.key === "Escape") {
    closeLensMenu();
  }
};

onMounted(() => {
  window.addEventListener("click", onWindowClick);
  window.addEventListener("keydown", onKeydown);
  workbench.setPromoRatio(workbench.promo.ratio || "1:1");
});

onUnmounted(() => {
  window.removeEventListener("click", onWindowClick);
  window.removeEventListener("keydown", onKeydown);
});
</script>
